import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import '../styles/AuthPages.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    identifier: '', // Can be email or phone
    password: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.identifier.trim()) {
      setError('Email or phone number is required');
      return false;
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    // Validate format
    const isEmail = /^\S+@\S+\.\S+$/.test(formData.identifier);
    const isPhone = /^[6-9]\d{9}$/.test(formData.identifier);

    if (!isEmail && !isPhone) {
      setError('Please enter a valid email address or 10-digit mobile number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await login({
        identifier: formData.identifier,
        password: formData.password
      });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const getPlaceholderText = () => {
    const identifier = formData.identifier.trim();
    if (!identifier) {
      return 'Email address or mobile number';
    }
    
    const isEmail = /^\S+@\S+\.\S+$/.test(identifier);
    const isPhone = /^[6-9]\d{9}$/.test(identifier);
    
    if (isEmail) {
      return 'Email address';
    } else if (isPhone) {
      return 'Mobile number';
    } else {
      return 'Email address or mobile number';
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>🔑 Welcome Back</h1>
            <p>Sign in to your Sahayak account</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            {/* Email or Phone */}
            <div className="form-group">
              <label htmlFor="identifier">Email or Mobile Number</label>
              <input
                type="text"
                id="identifier"
                name="identifier"
                value={formData.identifier}
                onChange={handleInputChange}
                placeholder={getPlaceholderText()}
                required
                autoComplete="username"
              />
              <small className="form-hint">
                Enter your email address or 10-digit mobile number
              </small>
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
            </div>

            <button 
              type="submit" 
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Don't have an account? 
              <Link to="/register" className="auth-link">Create Account</Link>
            </p>
          </div>
        </div>

        <div className="auth-illustration">
          <div className="illustration-content">
            <h2>Join Our Community</h2>
            <p>Connect with trusted helpers or start earning by helping others.</p>
            <div className="illustration-stats">
              <div className="stat">
                <span className="stat-number">1000+</span>
                <span className="stat-label">Happy Customers</span>
              </div>
              <div className="stat">
                <span className="stat-number">500+</span>
                <span className="stat-label">Verified Helpers</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 